import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { trim } from 'lodash/fp';

import { CloseButton, Header, Heading } from '../../../../../../mobile/structural/sheet';
import { UseSheetReturnType } from '../../../../../../mobile/structural/sheet/hooks/useSheet';
import { Label } from '../../../../../../../common/labels/labelTypes';
import { updateLabel } from '../../../../../../../common/labels/labelActions';
import { LABEL_NAME_MAX_LENGTH } from '../../../../../../../common/labels/labelConstants';
import ElementSimpleContentEditable from '../../../../../../components/editableTitle/ElementSimpleContentEditable';
import { stopPropagationOnly } from '../../../../../../utils/domUtil';

import '../../../../../../components/editableTitle/EditableTitle.scss';

type LabelEditSheetHeaderProps = {
    label: Label;
    sheetProps: UseSheetReturnType;
};

type ContentEditResult = {
    content: string;
};

const LabelEditSheetHeader = ({ label, sheetProps }: LabelEditSheetHeaderProps): ReactElement => {
    const dispatch = useDispatch();
    const [labelName, setLabelName] = useState(label.name);
    const [isEditingTitle, setIsEditingTitle] = useState(false);

    useEffect(() => {
        setLabelName(label.name);
        setIsEditingTitle(false);
    }, [label.name]);

    const cleanLabelName = (content: string | null | undefined) =>
        trim(String(content || '').substring(0, LABEL_NAME_MAX_LENGTH));

    const handleStopTitleEditing = (nextLabelName = labelName) => {
        setLabelName(nextLabelName);
        setIsEditingTitle(false);
    };

    const handleTitleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
        if (!isEditingTitle) return;

        const blurredLabelName = cleanLabelName(event.currentTarget.textContent);
        handleStopTitleEditing(blurredLabelName);
    };

    const onStopEditing = ({ content }: ContentEditResult) => {
        const nextLabelName = content || labelName;
        handleStopTitleEditing(nextLabelName);
    };

    const onUpdate = (content: string) => {
        const nextLabelName = content || labelName;
        setLabelName(nextLabelName);
        if (nextLabelName === label.name) return;

        dispatch(updateLabel(label._id, { name: nextLabelName }));
    };

    return (
        <Header>
            <Heading className="pb-1">
                <div
                    className="EditableTitle"
                    style={{
                        lineHeight: 'var(--font-size-ui-xxxlarge)',
                        fontFamily: 'var(--font-family-sans)',
                    }}
                >
                    <div onKeyDown={isEditingTitle ? stopPropagationOnly : undefined}>
                        <ElementSimpleContentEditable
                            className="editable-title"
                            enterKeyHint="Done"
                            isEditing={isEditingTitle}
                            isEditable={true}
                            onBlur={handleTitleBlur}
                            selectAllOnEdit
                            onStartEditing={() => setIsEditingTitle(true)}
                            onStopEditing={onStopEditing}
                            onUpdate={onUpdate}
                            cleanContent={cleanLabelName}
                        >
                            {labelName}
                        </ElementSimpleContentEditable>
                    </div>
                </div>
            </Heading>
            <CloseButton onClick={() => isEditingTitle && handleStopTitleEditing()} />
        </Header>
    );
};

export default LabelEditSheetHeader;
